import is from '@sindresorhus/is';

export type LayoutItem = {
    readonly id: string;
    readonly ideal: number;
    readonly height: number;
};

const resolvePivotIndex = (items: readonly LayoutItem[], activeId: string | null, viewportPivotIdx: number): number => {
    const activeIdx = is.null(activeId) ? -1 : items.findIndex((item) => item.id === activeId);
    if (activeIdx !== -1) return activeIdx;
    return viewportPivotIdx >= 0 ? viewportPivotIdx : 0;
};

const sweepBelow = (items: readonly LayoutItem[], pivotIdx: number, placed: Map<string, number>): void => {
    for (let i = pivotIdx + 1; i < items.length; i++) {
        const item = items[i];
        const prev = items[i - 1];
        if (is.undefined(item) || is.undefined(prev)) continue;
        const prevTop = placed.get(prev.id);
        if (is.undefined(prevTop)) continue;
        placed.set(item.id, Math.max(item.ideal, prevTop + prev.height));
    }
};

const sweepAbove = (items: readonly LayoutItem[], pivotIdx: number, placed: Map<string, number>): void => {
    for (let i = pivotIdx - 1; i >= 0; i--) {
        const item = items[i];
        const next = items[i + 1];
        if (is.undefined(item) || is.undefined(next)) continue;
        const nextTop = placed.get(next.id);
        if (is.undefined(nextTop)) continue;
        placed.set(item.id, Math.min(item.ideal, nextTop - item.height));
    }
};

export const computeLayout = (
    items: readonly LayoutItem[],
    activeId: string | null,
    viewportPivotIdx = 0,
): ReadonlyMap<string, number> => {
    const placed = new Map<string, number>();
    if (items.length === 0) return placed;

    const pivotIdx = resolvePivotIndex(items, activeId, viewportPivotIdx);
    const pivot = items[pivotIdx];
    if (is.undefined(pivot)) return placed;
    placed.set(pivot.id, is.null(activeId) ? Math.max(0, pivot.ideal) : pivot.ideal);

    sweepBelow(items, pivotIdx, placed);
    sweepAbove(items, pivotIdx, placed);

    return placed;
};
